/**
 * תור ניתוח הצילומים — "צלמי, ואני אדאג לשאר".
 *
 * ⚠️ הרקע (29.09.2026): אנסטסיה קיבלה "השרת עמוס" כמעט בכל צילום. שכבת ה-free של Gemini
 * עמוסה לסירוגין, ואף תזמון בשרת לא מבטיח תשובה בתוך 45 שניות. אבל היא לא צריכה תשובה
 * בתוך 45 שניות — היא צריכה שהצילום לא ילך לאיבוד ושלא תצטרך לנסות שוב בעצמה.
 * לכן כל ניתוח צילום עובר דרך התור: הצילום נשמר מקומית (IndexedDB), ניסיון ראשון יוצא
 * מיד, וכשל זמני (עומס / timeout / רשת) מתוזמן לניסיון חוזר אוטומטי במרווחים הולכים
 * וגדלים. התוצאה מחכה לה לאישור בבית; היא יכולה לסגור את האפליקציה בינתיים.
 *
 * מ-29.09.2026 גם **תיאור בטקסט ובהקלטה** עוברים כאן (`kind`), באותה התנהגות בדיוק: אפשר לצאת,
 * והתוצאה מחכה בבית. השם נשאר "צילומים" כדי לא לשבור רשומות שכבר שמורות במכשיר.
 *
 * ניסיונות רצים רק כשהאפליקציה גלויה (iOS עוצר רשת ברקע בכל מקרה), וממשיכים בחזרה אליה.
 * הצילומים של משתמשת אחת לא נראים למשתמשת אחרת באותו מכשיר (userId בכל רשומה).
 */
import { AiClarifyError, parseMeal, parseMealPhoto, parseMealVoice, type MealPhoto, type ParsedMealResult } from './ai';
import type { MealType } from './entries';
import { idbStore, type RecordStore } from '../lib/idbStore';
import { docVisibility, type Visibility } from '../lib/resumeFetch';
import { newId } from '../lib/netRetry';

export type JobStatus = 'working' | 'waiting' | 'ready' | 'clarify' | 'failed';
/** מה מנתחים. רשומה בלי kind (נשמרה לפני 29.09) היא צילום. */
export type JobKind = 'photo' | 'text' | 'voice';

export interface PhotoJob {
  id: string;
  userId: string;
  createdAt: number;
  /** שעת הצילום — לשם נרשמת הארוחה, גם אם אושרה שעה אחר כך */
  eatenAtIso: string;
  mealType: MealType;
  kind?: JobKind;
  photos: MealPhoto[];
  /** תיאור בטקסט (kind=text) */
  text?: string;
  /** הקלטה — WAV בבסיס 64 (kind=voice) */
  audio?: string;
  status: JobStatus;
  /** ניסיונות שנכשלו עד כה */
  attempts: number;
  /** מתי הניסיון האוטומטי הבא (סטטוס waiting) */
  nextAt: number;
  /** קוד השגיאה האחרונה (ai_busy / network / …) — להסבר למשתמשת */
  lastError?: string;
  result?: ParsedMealResult['meal'];
  clarify?: string;
}

export interface NewJob {
  kind?: JobKind;
  photos?: MealPhoto[];
  text?: string;
  audio?: string;
  mealType: MealType;
  eatenAtIso: string;
}

export function jobKind(j: Pick<PhotoJob, 'kind'>): JobKind {
  return j.kind ?? 'photo';
}

/** הניתוח עצמו, לפי סוג — צילום / טקסט / הקלטה */
export function analyzeJob(j: Pick<PhotoJob, 'kind' | 'photos' | 'text' | 'audio'>): Promise<ParsedMealResult> {
  const k = jobKind(j);
  if (k === 'text') return parseMeal(j.text ?? '');
  if (k === 'voice') return parseMealVoice(j.audio ?? '');
  return parseMealPhoto(j.photos);
}

/** מרווחי הניסיון החוזר, לפי מספר הכשלונות. אחרי האחרון — כל 5 דקות. */
export const RETRY_DELAYS_MS = [15_000, 30_000, 60_000, 120_000, 180_000, 300_000];
/** אחרי כך וכך כשלונות רצופים מפסיקים לבד (כ-45 דקות של אפליקציה פתוחה) — "לנסות שוב" ידני */
export const MAX_AUTO_ATTEMPTS = 12;
/** כמה ניתוחים רצים בו-זמנית (אחרי הפסקה ארוכה כמה צילומים מתעוררים יחד) */
const MAX_CONCURRENT = 2;

export function retryDelay(failures: number): number {
  return RETRY_DELAYS_MS[Math.min(failures, RETRY_DELAYS_MS.length) - 1] ?? RETRY_DELAYS_MS[0];
}

/** שגיאות שניסיון חוזר לא יתקן — הצילום עצמו לא הגיע תקין, או שאין הרשאה */
const FATAL = new Set(['bad_request', 'forbidden', 'ai_not_configured']);

type Classified = { kind: 'clarify'; clarify: string } | { kind: 'fatal' | 'retry'; code: string };

export function classifyError(e: unknown): Classified {
  if (e instanceof AiClarifyError) return { kind: 'clarify', clarify: e.clarify };
  const code = e instanceof Error && e.message ? e.message : 'unknown';
  return { kind: FATAL.has(code) ? 'fatal' : 'retry', code };
}

/** מה קרה בניסיון האחרון, במשפט אחד — ובאשמת מי (ראו aiErrorMsg ב-addSheet: לא להאשים את הצילום). */
export function jobReason(code?: string, kind: JobKind = 'photo'): string {
  const what = kind === 'text' ? 'בתיאור' : kind === 'voice' ? 'בהקלטה' : 'בצילום';
  if (code === 'ai_busy' || code === 'ai_timeout') return `גוגל עמוסה כרגע — התקלה אצלם, לא ${what}`;
  if (code === 'rate_limited') return `מכסת ה-AI נגמרה לעכשיו — לא בגלל ה${what.slice(1)}`;
  if (code === 'network' || code === 'interrupted') return 'אין חיבור יציב כרגע';
  if (code === 'no_session' || code === 'unauthorized') return 'החיבור לחשבון פג — צריך להיכנס מחדש';
  if (code === 'bad_request') return kind === 'photo' ? 'הצילום לא הגיע לשרת תקין — צריך לצלם שוב' : 'הבקשה לא הגיעה לשרת תקינה';
  return `הניתוח לא הצליח — התקלה אצלנו, לא ${what}`;
}

/** אותו דבר בשתי מילים — לכרטיס הקטן בבית */
export function jobReasonShort(code?: string): string {
  if (code === 'ai_busy' || code === 'ai_timeout') return 'גוגל עמוסה';
  if (code === 'rate_limited') return 'מכסת ה-AI נגמרה';
  if (code === 'network' || code === 'interrupted') return 'אין חיבור';
  if (code === 'no_session' || code === 'unauthorized') return 'צריך להיכנס מחדש';
  if (code === 'bad_request') return 'לא הגיע תקין';
  return 'תקלה בניתוח';
}

export interface QueueDeps {
  store: RecordStore<PhotoJob>;
  analyze: (job: PhotoJob) => Promise<ParsedMealResult>;
  vis?: Visibility;
  /** האם יש רשת — false רק כשהדפדפן בטוח שאין (navigator.onLine) */
  online?: () => boolean;
  onOnline?: (cb: () => void) => void;
}

type Watcher = (job: PhotoJob | undefined) => boolean;

export class PhotoQueue {
  private jobs = new Map<string, PhotoJob>();
  private running = new Set<string>();
  private listeners = new Set<(jobs: PhotoJob[]) => void>();
  private resultListeners = new Set<(job: PhotoJob) => void>();
  private watchers = new Map<string, Watcher>();
  private userId: string | null = null;
  /** עולה ב-start/stop — תוצאה של ניסיון שהתחיל לפני יציאה לא נכתבת אחריה */
  private epoch = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  /** פעולות האחסון בטור — מחיקה שנשלחה אחרי שמירה לא תקדים אותה (ולא תחזיר רשומה שנמחקה) */
  private io: Promise<unknown> = Promise.resolve();
  private readonly vis: Visibility;

  constructor(private deps: QueueDeps) {
    this.vis = deps.vis ?? docVisibility;
    this.vis.onChange(() => { if (!this.vis.hidden()) this.pump(); });
    deps.onOnline?.(() => this.pump());
  }

  /** טוען את הצילומים הממתינים של המשתמשת ומתחיל לעבוד עליהם. */
  async start(userId: string): Promise<void> {
    this.stop();
    this.userId = userId;
    const ep = this.epoch;
    let saved: PhotoJob[] = [];
    try { saved = await this.deps.store.all(); } catch { /* בלי התמדה — מתחילים ריק */ }
    if (ep !== this.epoch) return;
    for (const j of saved) {
      if (j.userId !== userId || this.jobs.has(j.id)) continue;
      // ניסיון שנקטע בסגירת האפליקציה — חוזר לתור, מיד
      this.jobs.set(j.id, j.status === 'working' ? { ...j, status: 'waiting', nextAt: Date.now() } : j);
    }
    this.emit();
    this.pump();
  }

  /** יציאה מהחשבון: שוכחים הכול מהזיכרון (לא מהאחסון — הם יחכו לה בכניסה הבאה). */
  stop(): void {
    this.epoch++;
    this.userId = null;
    this.jobs.clear();
    this.running.clear();
    this.watchers.clear();
    clearTimeout(this.timer);
    this.emit();
  }

  list(): PhotoJob[] {
    return [...this.jobs.values()].sort((a, b) => a.createdAt - b.createdAt);
  }

  get(id: string): PhotoJob | undefined {
    return this.jobs.get(id);
  }

  /** צילום חדש לניתוח — נשמר ויוצא לניסיון ראשון מיד. סינכרוני בכוונה: שתי לחיצות מהירות
   *  על "נתחי" לא יכולות לייצר שני צילומים (מסך ההמתנה מחליף את הכפתור לפני הלחיצה הבאה). */
  enqueue(input: NewJob): PhotoJob {
    const now = Date.now();
    const job: PhotoJob = {
      id: newId(),
      userId: this.userId ?? '',
      createdAt: now,
      eatenAtIso: input.eatenAtIso,
      mealType: input.mealType,
      kind: input.kind ?? 'photo',
      photos: input.photos ?? [],
      text: input.text,
      audio: input.audio,
      status: 'waiting',
      attempts: 0,
      nextAt: now,
    };
    this.jobs.set(job.id, job);
    // ⚠️ השמירה לא מעכבת את הניסיון הראשון: ניתוח מהיר לא מחכה לדיסק
    void this.persist(job);
    this.emit();
    this.pump();
    return job;
  }

  /** מוציא את הצילום מהתור ומחזיר אותו — מי שקורא לזה לוקח עליו אחריות (מסך האישור). */
  take(id: string): PhotoJob | undefined {
    const job = this.jobs.get(id);
    if (!job) return undefined;
    this.jobs.delete(id);
    this.watchers.delete(id);
    this.storeOp(() => this.deps.store.del(id));
    this.emit();
    return job;
  }

  /** ביטול מחיקה (undo) — מחזיר צילום שנלקח, במצב שבו היה. */
  restore(job: PhotoJob): void {
    if (job.userId !== (this.userId ?? '')) return;
    const back = job.status === 'working' ? { ...job, status: 'waiting' as const, nextAt: Date.now() } : job;
    this.jobs.set(back.id, back);
    void this.persist(back);
    this.emit();
    this.pump();
  }

  /** "לנסות שוב עכשיו" — גם לצילום שהתור כבר ויתר עליו. */
  retryNow(id: string): void {
    const job = this.jobs.get(id);
    if (!job || this.running.has(id) || job.status === 'ready') return;
    const attempts = job.status === 'failed' ? 0 : job.attempts;
    this.update(id, { status: 'waiting', nextAt: Date.now(), attempts });
    this.pump();
  }

  subscribe(cb: (jobs: PhotoJob[]) => void): () => void {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }

  /** תוצאה (מוכן / שאלה / כשל סופי) של צילום שאף מסך לא מחכה לו — להודעה גלובלית. */
  onResult(cb: (job: PhotoJob) => void): () => void {
    this.resultListeners.add(cb);
    return () => { this.resultListeners.delete(cb); };
  }

  /**
   * מסך שמחכה לצילום מסוים. ה-callback מחזיר false כשהמסך כבר לא מוצג — ואז התוצאה
   * עוברת להודעה הגלובלית (ולא נבלעת אצל מסך שנסגר).
   */
  watch(id: string, cb: Watcher): () => void {
    this.watchers.set(id, cb);
    return () => { if (this.watchers.get(id) === cb) this.watchers.delete(id); };
  }

  // ---------------------------------------------------------------- פנימי

  private storeOp(op: () => Promise<void>): Promise<void> {
    const p = this.io.then(op).catch(() => { /* ממשיכים מהזיכרון */ });
    this.io = p;
    return p;
  }

  private persist(job: PhotoJob): Promise<void> {
    return this.storeOp(() => this.deps.store.put(job));
  }

  private emit(): void {
    const jobs = this.list();
    for (const cb of this.listeners) cb(jobs);
  }

  private update(id: string, patch: Partial<PhotoJob>): PhotoJob | undefined {
    const cur = this.jobs.get(id);
    if (!cur) return undefined;
    const next = { ...cur, ...patch };
    this.jobs.set(id, next);
    if (next.status !== 'working') void this.persist(next);
    this.emit();
    const w = this.watchers.get(id);
    const handled = w ? w(next) : false;
    if (!handled) {
      if (w) this.watchers.delete(id);
      if (next.status === 'ready' || next.status === 'clarify' || next.status === 'failed') {
        for (const cb of this.resultListeners) cb(next);
      }
    }
    return next;
  }

  /** מתחיל את כל מה שהגיע זמנו (עד MAX_CONCURRENT), ומתזמן התעוררות לבא בתור. */
  private pump(): void {
    clearTimeout(this.timer);
    if (this.vis.hidden()) return;
    if (this.deps.online && !this.deps.online()) return;   // ה-online יעיר אותנו
    const now = Date.now();
    const waiting = this.list().filter((j) => j.status === 'waiting' && !this.running.has(j.id));
    for (const j of waiting) {
      if (this.running.size >= MAX_CONCURRENT) break;
      if (j.nextAt <= now) void this.run(j.id);
    }
    const later = this.list()
      .filter((j) => j.status === 'waiting' && !this.running.has(j.id))
      .map((j) => j.nextAt);
    if (later.length) this.timer = setTimeout(() => this.pump(), Math.max(0, Math.min(...later) - now));
  }

  private async run(id: string): Promise<void> {
    const job = this.jobs.get(id);
    if (!job) return;
    const ep = this.epoch;
    this.running.add(id);
    this.update(id, { status: 'working' });
    try {
      const { meal } = await this.deps.analyze(job);
      if (ep !== this.epoch || !this.jobs.has(id)) return;   // נמחק / יצאה מהחשבון בינתיים
      this.update(id, { status: 'ready', result: meal, lastError: undefined });
    } catch (e) {
      if (ep !== this.epoch || !this.jobs.has(id)) return;
      const c = classifyError(e);
      if (c.kind === 'clarify') {
        this.update(id, { status: 'clarify', clarify: c.clarify, lastError: undefined });
      } else {
        const attempts = job.attempts + 1;
        const giveUp = c.kind === 'fatal' || attempts >= MAX_AUTO_ATTEMPTS;
        this.update(id, giveUp
          ? { status: 'failed', attempts, lastError: c.code }
          : { status: 'waiting', attempts, lastError: c.code, nextAt: Date.now() + retryDelay(attempts) });
      }
    } finally {
      if (ep === this.epoch) {
        this.running.delete(id);
        this.pump();
      }
    }
  }
}

export const photoQueue = new PhotoQueue({
  store: idbStore<PhotoJob>('litebite', 'photoJobs'),
  analyze: analyzeJob,
  online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
  onOnline: (cb) => { if (typeof window !== 'undefined') window.addEventListener('online', cb); },
});
